import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SimonCustomerModal from '../components/SimonCustomerModal';

const SimonCustomerPage = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    const fetchCustomers = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/simoncustomers?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });

            if (response.data.success) {
                setCustomers(response.data.data);
                setTotalCustomers(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch Simon customers: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    useEffect(() => {
        const fetchCustomerOrders = async () => {
            if (!showViewModal || !selectedCustomer?.customerID) {
                setCustomerOrders([]);
                return;
            }

            try {
                setOrdersLoading(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/simonorders/customer/${encodeURIComponent(selectedCustomer.customerID)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCustomerOrders(response.data.data || []);
                }
            } catch (err) {
                setCustomerOrders([]);
            } finally {
                setOrdersLoading(false);
            }
        };

        fetchCustomerOrders();
    }, [showViewModal, selectedCustomer?.customerID]);

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedCustomer) return;
        try {
            setDeleteLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/simoncustomers/${selectedCustomer._id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setShowDeleteModal(false);
            setSelectedCustomer(null);
            fetchCustomers(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to delete Simon customer: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatTelegram = (telegrams = []) => {
        if (!telegrams.length) return 'N/A';
        return telegrams
            .map((item) => [item.tgName, item.tgNumber, item.tgID].filter(Boolean).join(' - '))
            .filter(Boolean)
            .join(', ');
    };

    const formatCreateMMYY = (value) => {
        if (!value) return 'N/A';
        const d = new Date(value);
        if (!Number.isFinite(d.getTime())) return 'N/A';
        return d.toLocaleDateString('en-SG', { month: '2-digit', year: '2-digit' });
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Simon Customers</h1>
                        <p className="text-gray-600">Total: {totalCustomers} customers</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Customer
                    </button>
                </div>

                <div className="mb-6">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by customer ID, names, phones, WhatsApp, address, or telegram..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                            <FiSearch className="mr-2" />
                            Search
                        </button>
                        <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                            Clear
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {showCreateModal && (
                    <SimonCustomerModal
                        mode="create"
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchCustomers(currentPage, searchTerm);
                        }}
                        onCancel={() => setShowCreateModal(false)}
                    />
                )}

                {showEditModal && selectedCustomer && (
                    <SimonCustomerModal
                        mode="edit"
                        customer={selectedCustomer}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedCustomer(null);
                            fetchCustomers(currentPage, searchTerm);
                        }}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedCustomer(null);
                        }}
                    />
                )}

                {showDeleteModal && selectedCustomer && (
                    <DeleteConfirmationModal
                        title="Delete Simon Customer"
                        message="Are you sure you want to delete this customer?"
                        itemName={selectedCustomer.customerID}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setSelectedCustomer(null);
                        }}
                    />
                )}

                {showViewModal && selectedCustomer && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-5xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium">
                                    Simon Customer Details - <span className="text-blue-700 font-bold">{selectedCustomer.customerID}</span>
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowViewModal(false);
                                        setSelectedCustomer(null);
                                        setCustomerOrders([]);
                                    }}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Contact Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div><span className="font-semibold text-gray-700">Names:</span> <span className="text-blue-700">{selectedCustomer.names?.join(', ') || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Create MMYY:</span> <span className="text-blue-700">{formatCreateMMYY(selectedCustomer.createMMYY)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Phone:</span> <span className="text-blue-700">{selectedCustomer.phoneNumbers?.join(', ') || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">WhatsApp:</span> <span className="text-blue-700">{selectedCustomer.whatsappNumbers?.join(', ') || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Address:</span> <span className="text-blue-700">{selectedCustomer.addresses?.join(', ') || 'N/A'}</span></div>
                                        <div className="md:col-span-2"><span className="font-semibold text-gray-700">Telegram:</span> <span className="text-blue-700">{formatTelegram(selectedCustomer.telegrams)}</span></div>
                                    </div>
                                </section>

                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex flex-wrap gap-6 text-sm mb-3">
                                        <span className="text-gray-700">Total Orders: <span className="font-semibold text-blue-700">{selectedCustomer.orderCount || 0}</span></span>
                                        <span className="text-gray-700">Total Revenue: <span className="font-semibold text-blue-700">${Number(selectedCustomer.totalRevenue || 0).toFixed(2)}</span></span>
                                    </div>
                                    <h4 className="text-lg font-medium mb-3">Orders</h4>
                                    <div className="overflow-x-auto">
                                        {ordersLoading ? (
                                            <div className="py-4 text-center text-gray-500 text-sm">Loading orders...</div>
                                        ) : customerOrders.length === 0 ? (
                                            <div className="py-4 text-center text-gray-500 text-sm">No orders found</div>
                                        ) : (
                                            <table className="min-w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left">Order Date</th>
                                                        <th className="px-3 py-2 text-left">Supplier</th>
                                                        <th className="px-3 py-2 text-left">Status</th>
                                                        <th className="px-3 py-2 text-right">Total Price</th>
                                                        <th className="px-3 py-2 text-right">Total Cost</th>
                                                        <th className="px-3 py-2 text-right">Total Comms</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {customerOrders.map((order) => (
                                                        <tr key={order._id}>
                                                            <td className="px-3 py-2">{order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-SG') : 'N/A'}</td>
                                                            <td className="px-3 py-2">{[order.supplier, order.supplierSource].filter(Boolean).join(' - ') || 'N/A'}</td>
                                                            <td className="px-3 py-2">{order.orderStatus || 'N/A'}</td>
                                                            <td className="px-3 py-2 text-right">${Number(order.totalPrice || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 text-right">${Number(order.totalCost || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 text-right">${Number(order.totalComms || 0).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Create MMYY</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Names</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">Loading...</td>
                                </tr>
                            ) : customers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">No Simon customers found</td>
                                </tr>
                            ) : (
                                customers.map((customer) => (
                                    <tr key={customer._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{customer.customerID}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCreateMMYY(customer.createMMYY)}</td>
                                        <td className="px-6 py-4 text-sm">{customer.names?.join(', ') || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm">{customer.phoneNumbers?.join(', ') || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm">{customer.whatsappNumbers?.join(', ') || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{customer.orderCount || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(customer.totalRevenue || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => { setSelectedCustomer(customer); setShowViewModal(true); }} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details">
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => { setSelectedCustomer(customer); setShowEditModal(true); }} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Customer">
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => { setSelectedCustomer(customer); setShowDeleteModal(true); }} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Customer">
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default SimonCustomerPage;
